import { Link } from "react-router-dom";
import { RefreshCw, Loader2, ChevronDown, Radio, UploadCloud, BarChart3, Target, Gauge, Filter, PhoneCall, Wallet, TrendingDown, Users, Timer, Smile, BadgeCheck, ShieldCheck, Activity, Clock, CalendarCheck, AlarmClock, MessageSquare, AlertTriangle, Percent } from "lucide-react";

export const tooltipStyle = { background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))", fontFamily: "'JetBrains Mono', monospace" };
export const tickStyle = { fill: "#7a8fb5", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" };

const ICON_RULES = [
  [/sale/, BarChart3], [/target|attain/, Target], [/run-rate|rate/, Gauge], [/conver/, Filter], [/call|dial|answer/, PhoneCall],
  [/commission|qar|revenue/, Wallet], [/churn|late|miss|deficit/, TrendingDown], [/retention|agent|headcount|team/, Users],
  [/aht|handle|time in|duration/, Timer], [/voc|csat|nps/, Smile], [/pmt|qsl|quality|score/, BadgeCheck], [/sla|adherence/, ShieldCheck],
  [/volume|chat|whatsapp/, MessageSquare], [/critical|error/, AlertTriangle], [/attend|days/, CalendarCheck], [/grace|punct/, AlarmClock],
  [/clock|hour|shift/, Clock], [/%|pct/, Percent],
];
export const kpiIcon = (label = "") => (ICON_RULES.find(([re]) => re.test(String(label).toLowerCase())) || [null, Activity])[1];

export const Badge = ({ tone = "neutral", children, testid }) => {
  const cls = { good: "bg-thmint/15 text-thmint", bad: "bg-thcoral/15 text-thcoral", warn: "bg-thamber/15 text-thamber", info: "bg-thblue/15 text-thblue", neutral: "bg-thhover/8 text-thdim" }[tone];
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase tracking-[0.12em] ${cls}`}>{children}</span>;
};

export const KpiCard = ({ testid, label, value, sub, color, icon, badge, children, delay = 0, big = false }) => {
  const Icon = icon || kpiIcon(label);
  return (
    <div data-testid={testid} className="mk-card mk-fade p-5" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-start justify-between gap-3">
        <p className="mk-label">{label}</p>
        <div className="flex items-center gap-2">
          {badge}
          <span className="mk-icon-tile" style={{ color, background: `${color}1f` }}><Icon className="h-3.5 w-3.5" /></span>
        </div>
      </div>
      <p className={`mt-3 font-heading font-bold leading-none tracking-tight ${big ? "text-3xl" : "text-[26px]"}`} style={{ color }}>{value}</p>
      {children}
      {sub && <p className="mk-sub mt-3">{sub}</p>}
    </div>
  );
};

export const ModuleHeader = ({ testid, title, subtitle, accent, context, meta, onRefresh, busy }) => (
  <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 data-testid={testid} className="font-heading text-3xl font-extrabold tracking-tight text-thstrong sm:text-4xl">
        {title} {subtitle && <span className="ml-1 text-lg font-semibold" style={{ color: accent }}>{subtitle}</span>}
      </h1>
      <p className="mk-label mt-2 tracking-[0.25em]">{context}</p>
    </div>
    {onRefresh && (
      <div className="flex items-center gap-3">
        {meta && <p className="mk-sub hidden sm:block">{meta}</p>}
        <button data-testid={`${testid?.split("-")[0] || "module"}-refresh`} onClick={onRefresh} disabled={busy} className="mk-btn-ghost">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
    )}
  </div>
);

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (d) => {
  if (!d) return null;
  const [y, m, day] = String(d).split("-").map(Number);
  if (!m) return String(d);
  return day ? `${MON[m - 1]} ${day}` : `${MON[m - 1]} ${y}`;
};

export const FeedChips = ({ testid, coverage = {}, months = [], hint, children }) => {
  const feeds = Object.entries(coverage).filter(([, v]) => v !== undefined);
  return (
    <div data-testid={testid} className="mb-4 flex flex-wrap items-center gap-2">
      <span className="mk-label mr-1 inline-flex items-center gap-1.5"><Radio className="h-3 w-3 text-thcyan" /> Feed coverage</span>
      {feeds.map(([k, v]) => (
        <span key={k} data-testid={`feed-chip-${k}`} className={`rounded-full px-2.5 py-1 font-mono-hud text-[10px] ${v ? "bg-thblue/12 text-thblue" : "bg-thcoral/10 text-thcoral"}`}>
          {k.replace(/_/g, " ")} · {v ? shortDate(v) : "missing"}
        </span>
      ))}
      {months.map((m) => <span key={m} data-testid={`months-chip-${m}`} className="rounded-full bg-thmint/12 px-2.5 py-1 font-mono-hud text-[10px] text-thmint">{m} ✓</span>)}
      {children}
      {hint && <span className="mk-sub ml-auto hidden lg:block">{hint}</span>}
    </div>
  );
};

export const Toolbar = ({ children, testid }) => (
  <div data-testid={testid} className="mb-5 flex flex-wrap items-center gap-2.5">{children}</div>
);

export const PillSelect = ({ testid, value, onChange, children, icon: Icon }) => (
  <div className="relative inline-flex items-center">
    {Icon && <Icon className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-thcyan" />}
    <select data-testid={testid} value={value} onChange={onChange} className={`mk-pill mk-pill-select ${Icon ? "!pl-8" : ""}`}>{children}</select>
    <ChevronDown className="pointer-events-none absolute right-3 h-3.5 w-3.5 text-thdim" />
  </div>
);

export const PillInput = ({ testid, ...props }) => <input data-testid={testid} {...props} className="mk-pill mk-pill-select !pr-3 text-thstrong" />;

export const DateRangePills = ({ prefix, from, to, setFrom, setTo, presets = [] }) => (
  <>
    <div className="flex items-center gap-1.5">
      <PillInput testid={`${prefix}-filter-from`} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
      <span className="text-xs text-thdim">→</span>
      <PillInput testid={`${prefix}-filter-to`} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
    </div>
    <div className="flex flex-wrap gap-1">
      {presets.map((p) => {
        const active = from === p.from && to === p.to;
        return (
          <button key={p.id} data-testid={`${prefix}-preset-${p.id}`} onClick={() => { setFrom(p.from); setTo(p.to); }}
            className={`rounded-full px-3 py-1.5 font-mono-hud text-[10px] uppercase tracking-[0.1em] transition-colors ${active ? "bg-thcyan/15 text-thcyan" : "text-thmute hover:bg-thhover/8 hover:text-thstrong"}`}>
            {p.label}
          </button>
        );
      })}
    </div>
  </>
);

export const Segmented = ({ testidPrefix, options, value, onChange, className = "" }) => (
  <div className={`mk-seg ${className}`}>
    {options.map((o) => (
      <button key={o} data-testid={`${testidPrefix}-${o.toLowerCase()}`} data-active={value === o} onClick={() => onChange(o)}>{o}</button>
    ))}
  </div>
);

export const SectionTabs = ({ testidPrefix, tabs, value, onChange, accent }) => (
  <div className="mb-6 flex gap-1 overflow-x-auto border-b border-thborder/10">
    {tabs.map((t) => (
      <button key={t.id} data-testid={`${testidPrefix}-${t.id}`} onClick={() => onChange(t.id)}
        className={`relative whitespace-nowrap px-4 py-3 font-heading text-sm font-semibold transition-colors ${value === t.id ? "text-thstrong" : "text-thmute hover:text-thbody"}`}>
        {t.label}
        {value === t.id && <span className="absolute inset-x-2 bottom-0 h-[2px] rounded-full" style={{ background: accent || "rgb(var(--th-cyan))" }} />}
      </button>
    ))}
  </div>
);

export const EmptyState = ({ testid, icon: Icon = UploadCloud, title, body, action, isAdmin }) => (
  <div className="mk-card mk-fade flex flex-col items-center px-6 py-16 text-center">
    <span className="mk-icon-tile mb-4 h-12 w-12 rounded-2xl bg-thcyan/10 text-thcyan"><Icon className="h-5 w-5" /></span>
    <p className="mk-label mb-2 tracking-[0.25em]">{title}</p>
    <p data-testid={testid} className="max-w-md text-sm text-thmute">{body}</p>
    {isAdmin && action && <Link to={action.to} data-testid={`${testid}-action`} className="mk-btn-primary mt-6">{action.label}</Link>}
  </div>
);

export const Loading = ({ label = "Crunching uploaded months…" }) => (
  <div className="space-y-4" data-testid="module-loading">
    <div className="flex items-center gap-3 text-thcyan"><Loader2 className="h-5 w-5 animate-spin" /><p className="mk-label">{label}</p></div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="mk-skel h-28" />)}</div>
    <div className="mk-skel h-56" />
  </div>
);

export const Panel = ({ title, right, children, className = "", testid }) => (
  <div data-testid={testid} className={`mk-card p-5 ${className}`}>
    {(title || right) && (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {title && <p className="mk-label tracking-[0.25em]">{title}</p>}
        {right}
      </div>
    )}
    {children}
  </div>
);
